"use client"

import { useState } from "react"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  formatCurrency,
  formatMultiple,
  parseNum,
} from "./calculator-ui"

/**
 * LTV = monthly ARPA × gross-margin-fraction × average customer lifetime (months).
 * LTV:CAC ratio = LTV ÷ CAC. Health: ≥3 good, 1–3 warn, <1 bad.
 * The margin input is a percentage; we divide by 100 first.
 * Default example: $100 ARPA × 0.80 margin × 24 months = $1,920 LTV; ÷ $400 CAC = 4.8×.
 */
export function LtvCacWidget() {
  const [arpa, setArpa] = useState("100")
  const [marginPct, setMarginPct] = useState("80")
  const [lifetime, setLifetime] = useState("24")
  const [cac, setCac] = useState("400")

  const a = parseNum(arpa)
  const m = parseNum(marginPct)
  const l = parseNum(lifetime)
  const c = parseNum(cac)

  const fraction = m !== null ? m / 100 : null
  const ltv =
    a !== null && fraction !== null && l !== null ? a * fraction * l : NaN
  const ratio =
    Number.isFinite(ltv) && c !== null && c > 0 ? ltv / c : NaN

  const tone = Number.isFinite(ratio)
    ? ratio >= 3
      ? "good"
      : ratio >= 1
        ? "warn"
        : "bad"
    : "neutral"

  const verdict = Number.isFinite(ratio)
    ? ratio >= 3
      ? "Healthy — you recover acquisition cost with strong margin to reinvest."
      : ratio >= 1
        ? "Thin — you make money but have little room to scale spend."
        : "Underwater — each customer costs more than they're worth."
    : "Enter ARPA, margin, lifetime, and a non-zero CAC."

  return (
    <CalculatorCard title="LTV:CAC Ratio Calculator">
      <CalculatorGrid>
        <NumberField
          id="ltv-arpa"
          label="Monthly revenue per customer (ARPA)"
          value={arpa}
          onChange={setArpa}
          prefix="$"
        />
        <NumberField
          id="ltv-margin"
          label="Gross margin"
          value={marginPct}
          onChange={setMarginPct}
          suffix="%"
        />
        <NumberField
          id="ltv-lifetime"
          label="Avg. customer lifetime"
          value={lifetime}
          onChange={setLifetime}
          suffix="mo"
        />
        <NumberField
          id="ltv-cac"
          label="CAC"
          value={cac}
          onChange={setCac}
          prefix="$"
        />
      </CalculatorGrid>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <ResultStat
          label="Lifetime value (LTV)"
          value={formatCurrency(ltv)}
        />
        <ResultStat
          label="LTV : CAC ratio"
          value={Number.isFinite(ratio) ? formatMultiple(ratio) : "—"}
          tone={tone}
          caption={verdict}
        />
      </div>

      <FormulaNote>LTV = Monthly ARPA × Gross margin (fraction) × Lifetime (months). Ratio = LTV ÷ CAC. Aim for ≥ 3×.</FormulaNote>
    </CalculatorCard>
  )
}
